import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isArray, mapValues } from 'lodash-es';
import { Observable } from 'rxjs';
import { WINDOW } from 'shared/framework/data-access/angular';
import { CurrentRoute } from '../../models/current-route/current-route.model';
import { ExternalRoute } from '../../models/external-route/external-route.model';
import { Route } from '../../models/route/route.model';

@Injectable({
    providedIn: 'root',
})
export class BaseRouterService {
    #router = inject(Router);
    #window = inject(WINDOW);
    #activatedRoute = inject(ActivatedRoute);

    createExternalRoute = (url: string, label: string): ExternalRoute => ({
        href: url,
        label: label,
        goTo: () => {
            this.#window.location.href = url;
        },
    });

    createRoute = <Params extends Record<string, string | undefined>>(
        commands: string[],
        label: string,
        replaceUrl?: boolean,
        queryParams?: Params,
    ): Route<Params> => ({
        routerLink: commands,
        label: label,
        queryParams: (queryParams ?? {}) as Params,
        goTo: () => {
            this.#router
                .navigate(commands, {
                    replaceUrl,
                    queryParams,
                })
                .catch((err: unknown) => {
                    console.error(err);
                });
        },
        isActive: () => {
            const tree = this.#router.createUrlTree(commands);

            // Angular 21.1 deprecates Router.isActive in favour of a standalone
            // isActive(url, router, opts) that returns Signal<boolean>. This helper is
            // synchronous and must return a plain boolean, so adopting it means either
            // creating a signal per call or making this reactive — a design change,
            // and this upgrade preserves observable behaviour. Tracked separately.
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            return this.#router.isActive(tree, {
                paths: 'exact',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored',
            });
        },
    });

    createCurrentRoute = <Params extends Record<string, string | undefined>>(
        queryParams?: Params,
        replaceUrl?: boolean,
    ): CurrentRoute<Params> => ({
        queryParams: this.#untypedParamsToStringParams(
            this.#activatedRoute.snapshot.queryParams,
        ) as Params,
        queryParamsChanged: this.#activatedRoute
            .queryParams as Observable<Params>,
        goTo: () => {
            this.#router
                .navigate([], {
                    relativeTo: this.#activatedRoute,
                    queryParams: {
                        ...queryParams,
                    },
                    queryParamsHandling: 'merge',
                    replaceUrl,
                })
                .catch((err: unknown) => {
                    console.error(err);
                });
        },
    });

    #untypedParamsToStringParams = (
        params: Record<string, string | string[] | undefined>,
    ): Record<string, string | undefined> =>
        mapValues(params, (value) => {
            if (isArray(value)) {
                if (value.length) {
                    return value[0];
                } else {
                    return undefined;
                }
            }

            return value;
        });
}
